import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, XIcon, TrashIcon, ShoppingCartIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
const AddOrder = () => {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const sidebarRef = useRef(null);
  const [menuItems, setMenuItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isQuantityModalOpen, setIsQuantityModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [quantityError, setQuantityError] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  // Load menu items
  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      navigate('/login');
      return;
    }
    // Get menu items from localStorage
    const savedMenuItems = localStorage.getItem('menuItems');
    if (savedMenuItems) {
      setMenuItems(JSON.parse(savedMenuItems));
    } else {
      // Sample menu items if none in localStorage
      const sampleMenuItems = [{
        id: 1,
        name: 'Kare-Kare',
        category: 'Main Dishes',
        price: 299.0,
        status: 'Available',
        image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wfcm5TzpGnGaMfZVrn6ZSk/karekare.jpg',
        description: 'A Filipino stew with a thick peanut sauce, oxtail, and vegetables.'
      }, {
        id: 2,
        name: 'Bulalo',
        category: 'Main Dishes',
        price: 320.0,
        status: 'Available',
        image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xygfVbdEWgWP3jK3z6Uqid/Bulalo.jpg',
        description: 'A light-colored soup made by cooking beef shanks and bone marrow with corn, cabbage, green beans and potatoes.'
      }, {
        id: 3,
        name: 'Sinigang',
        category: 'Main Dishes',
        price: 280.0,
        status: 'Available',
        image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uxM8pHhbUXC3pGWHzJs6Tf/sinigang.jpg',
        description: 'A sour soup with pork, vegetables, and tamarind.'
      }, {
        id: 4,
        name: 'Lumpiang Shanghai',
        category: 'Side Dishes',
        price: 150.0,
        status: 'Available',
        image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mcmzLGmXiKV1sfomKhiKnu/lumpi.jpg',
        description: 'Filipino spring rolls filled with ground pork and vegetables.'
      }, {
        id: 7,
        name: 'Leche Flan',
        category: 'Desserts',
        price: 90.0,
        status: 'Available',
        image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5xxBshb8UQQM1avDETHe8p/lecheflan.jpg',
        description: 'Filipino-style crème caramel made with condensed milk and egg yolks.'
      }, {
        id: 8,
        name: 'Bulalo House Coolers',
        category: 'Beverages',
        price: 85.0,
        status: 'Available',
        image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hjDux2hW8aSsDw793FRQPM/489862575_122140821638423180_6935283752305353880_n.jpg',
        description: 'Refreshing layered drink with flavored syrups, perfect to pair with Filipino dishes.'
      }, {
        id: 9,
        name: 'Calamansi Juice',
        category: 'Beverages',
        price: 65.0,
        status: 'Available',
        image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uoT5jSdYi5K9vA5bdPWizb/calamansi.jpg',
        description: 'Fresh Filipino citrus juice, sweet and tangy.'
      }];
      setMenuItems(sampleMenuItems);
    }
    setIsLoading(false);
  }, [user, navigate]);
  // Handle sidebar slide animation
  useEffect(() => {
    if (isSidebarOpen && sidebarRef.current) {
      sidebarRef.current.style.transform = 'translateX(0)';
    } else if (sidebarRef.current) {
      sidebarRef.current.style.transform = 'translateX(100%)';
    }
  }, [isSidebarOpen]);
  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Handle item selection
  const selectItem = item => {
    setCurrentItem(item);
    setQuantity(1);
    setQuantityError('');
    setIsQuantityModalOpen(true);
  };
  // Validate quantity
  const validateQuantity = value => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue <= 0) {
      setQuantityError('Quantity must be greater than 0');
      return false;
    }
    setQuantityError('');
    return true;
  };
  // Handle quantity change
  const handleQuantityChange = e => {
    const value = e.target.value;
    setQuantity(value);
    validateQuantity(value);
  };
  // Add item to order
  const addItemToOrder = () => {
    if (!validateQuantity(quantity)) return;
    const newItem = {
      ...currentItem,
      quantity: parseInt(quantity),
      subtotal: currentItem.price * parseInt(quantity)
    };
    setSelectedItems([...selectedItems, newItem]);
    setIsQuantityModalOpen(false);
  };
  // Confirm delete
  const confirmDelete = item => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };
  // Delete item from order
  const deleteItem = () => {
    setSelectedItems(selectedItems.filter(item => item.id !== itemToDelete.id));
    setIsDeleteModalOpen(false);
  };
  // Calculate order total
  const calculateTotal = () => {
    return selectedItems.reduce((total, item) => total + item.subtotal, 0);
  };
  // Calculate total quantity
  const calculateTotalQuantity = () => {
    return selectedItems.reduce((total, item) => total + item.quantity, 0);
  };
  // Place order
  const placeOrder = () => {
    if (selectedItems.length === 0) return;
    // Create order object
    const order = {
      id: `ORD-${Date.now().toString().slice(-5)}`,
      date: new Date().toISOString(),
      items: selectedItems,
      subtotal: calculateTotal(),
      deliveryFee: 20.0,
      total: calculateTotal() + 20.0,
      status: 'Pending',
      customerName: user ? `${user.name}` : 'Guest',
      customerEmail: user ? user.email : 'guest@example.com'
    };
    // Save order to localStorage
    const orders = JSON.parse(localStorage.getItem('orders') || '[]');
    orders.push(order);
    localStorage.setItem('orders', JSON.stringify(orders));
    // Clear selected items
    setSelectedItems([]);
    // Close sidebar
    setIsSidebarOpen(false);
    // Redirect to orders list
    navigate('/orders');
  };
  if (isLoading) {
    return <div className="flex justify-center items-center min-h-screen bg-amber-50" data-id="element-310">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800" data-id="element-311"></div>
      </div>;
  }
  return <div className="bg-amber-50 min-h-screen" data-id="element-312">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-313">
        <div className="flex justify-between items-center mb-6" data-id="element-314">
          <h1 className="text-2xl font-semibold text-amber-900" data-id="element-315">
            Add New Order
          </h1>
          <button onClick={toggleSidebar} className="px-4 py-2 bg-amber-800 text-white rounded-md hover:bg-amber-900 flex items-center" data-id="element-316">
            <PlusIcon className="h-5 w-5 mr-1" data-id="element-317" />
            Add Transaction
          </button>
        </div>
        {/* Products List */}
        <div className="bg-white rounded-lg shadow-md p-6" data-id="element-318">
          <h2 className="text-lg font-semibold text-amber-900 mb-4" data-id="element-319">
            Available Products
          </h2>
          <div className="overflow-x-auto" data-id="element-320">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-321">
              <thead className="bg-gray-50" data-id="element-322">
                <tr data-id="element-323">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-324">
                    Product
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-325">
                    Category
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-326">
                    Price
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-327">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-328">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-329">
                {menuItems.map(item => <tr key={item.id} data-id="element-330">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-331">
                      <div className="flex items-center" data-id="element-332">
                        <div className="flex-shrink-0 h-10 w-10" data-id="element-333">
                          <img className="h-10 w-10 rounded-full object-cover" src={item.image} alt={item.name} data-id="element-334" />
                        </div>
                        <div className="ml-4" data-id="element-335">
                          <div className="text-sm font-medium text-gray-900" data-id="element-336">
                            {item.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-337">
                            {item.description.substring(0, 50)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-338">
                      <div className="text-sm text-gray-900" data-id="element-339">
                        {item.category}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-340">
                      <div className="text-sm text-gray-900" data-id="element-341">
                        ₱{item.price.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-342">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${item.status === 'Available' ? 'bg-green-100 text-green-800' : item.status === 'Low Stock' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-343">
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-344">
                      <button onClick={() => selectItem(item)} disabled={item.status === 'Out of Stock'} className={`px-3 py-1 rounded ${item.status === 'Out of Stock' ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-amber-800 text-white hover:bg-amber-900'}`} data-id="element-345">
                        Add
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Sidebar for Order Confirmation */}
      <div ref={sidebarRef} className="fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-lg transform translate-x-full transition-transform duration-300 ease-in-out z-40" data-id="element-346">
        <div className="p-6 h-full flex flex-col" data-id="element-347">
          <div className="flex justify-between items-center mb-6" data-id="element-348">
            <h2 className="text-lg font-semibold text-amber-900" data-id="element-349">
              Confirm Order
            </h2>
            <button onClick={toggleSidebar} className="text-gray-500 hover:text-gray-700" data-id="element-350">
              <XIcon className="h-6 w-6" data-id="element-351" />
            </button>
          </div>
          {selectedItems.length === 0 ? <div className="flex-grow flex flex-col items-center justify-center text-gray-500" data-id="element-352">
              <ShoppingCartIcon className="h-16 w-16 mb-4" data-id="element-353" />
              <p data-id="element-354">No items added yet</p>
              <p className="text-sm mt-2" data-id="element-355">Add items from the product list</p>
            </div> : <>
              <div className="flex-grow overflow-y-auto" data-id="element-356">
                <div className="space-y-4" data-id="element-357">
                  {selectedItems.map(item => <div key={item.id} className="flex justify-between items-center p-3 border border-gray-200 rounded-md" data-id="element-358">
                      <div data-id="element-359">
                        <h3 className="font-medium" data-id="element-360">{item.name}</h3>
                        <p className="text-sm text-gray-600" data-id="element-361">
                          {item.quantity} x ₱{item.price.toFixed(2)}
                        </p>
                      </div>
                      <div className="flex items-center" data-id="element-362">
                        <span className="font-medium mr-4" data-id="element-363">
                          ₱{item.subtotal.toFixed(2)}
                        </span>
                        <button onClick={() => confirmDelete(item)} className="text-red-600 hover:text-red-800" data-id="element-364">
                          <TrashIcon className="h-5 w-5" data-id="element-365" />
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-366">
                <div className="flex justify-between text-sm mb-2" data-id="element-367">
                  <span className="text-gray-600" data-id="element-368">Total Items:</span>
                  <span className="font-medium" data-id="element-369">
                    {calculateTotalQuantity()}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-2" data-id="element-370">
                  <span className="text-gray-600" data-id="element-371">Subtotal:</span>
                  <span className="font-medium" data-id="element-372">
                    ₱{calculateTotal().toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-4" data-id="element-373">
                  <span className="text-gray-600" data-id="element-374">Delivery Fee:</span>
                  <span className="font-medium" data-id="element-375">₱20.00</span>
                </div>
                <div className="flex justify-between font-semibold text-lg" data-id="element-376">
                  <span data-id="element-377">Total:</span>
                  <span data-id="element-378">₱{(calculateTotal() + 20).toFixed(2)}</span>
                </div>
                <button onClick={placeOrder} disabled={selectedItems.length === 0} className={`mt-6 w-full py-2 rounded-md ${selectedItems.length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-amber-800 text-white hover:bg-amber-900'}`} data-id="element-379">
                  Confirm Order
                </button>
              </div>
            </>}
        </div>
      </div>
      {/* Quantity Modal */}
      {isQuantityModalOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-380">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-id="element-381">
            <h3 className="text-lg font-semibold text-amber-900 mb-4" data-id="element-382">
              {currentItem.name} - ₱{currentItem.price.toFixed(2)}
            </h3>
            <div className="mb-4" data-id="element-383">
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-384">
                Quantity
              </label>
              <input type="number" id="quantity" min="1" value={quantity} onChange={handleQuantityChange} className={`w-full px-3 py-2 border ${quantityError ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500`} data-id="element-385" />
              {quantityError && <p className="mt-1 text-sm text-red-600" data-id="element-386">{quantityError}</p>}
            </div>
            <div className="flex justify-end space-x-3" data-id="element-387">
              <button onClick={() => setIsQuantityModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-388">
                Cancel
              </button>
              <button onClick={addItemToOrder} disabled={!!quantityError || !quantity} className={`px-4 py-2 rounded-md ${quantityError || !quantity ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-amber-800 text-white hover:bg-amber-900'}`} data-id="element-389">
                Confirm
              </button>
            </div>
          </div>
        </div>}
      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-390">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-id="element-391">
            <h3 className="text-lg font-semibold text-amber-900 mb-2" data-id="element-392">
              Confirm Deletion
            </h3>
            <p className="text-gray-600 mb-4" data-id="element-393">
              Are you sure you want to remove {itemToDelete?.name} from your
              order?
            </p>
            <div className="flex justify-end space-x-3" data-id="element-394">
              <button onClick={() => setIsDeleteModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-395">
                Cancel
              </button>
              <button onClick={deleteItem} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-396">
                Delete
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
export default AddOrder;